import type { SchemaData, SchemaStation } from '../schema/types'
import { lineHexForTheme } from '../schema/lineColor'

/**
 * Pure function: given schema + URL path slug + optional `?line=` and
 * `?station=` query values, resolve which station record to render on
 * the panel.
 *
 * Semantics (v1.23+ schema):
 * - All records in a multi-line cluster share the same `slug`.
 * - Client looks up by slug → finds the cluster (N records, one per line).
 * - Pick the right record by:
 *   1. `?station=<id>` → exact id match within cluster (handles
 *      multi-platform-same-line clusters, e.g. Euston on Northern line
 *      has two platforms northern.940GZZLUEUS-0 / northern.940GZZLUEUS-1, and
 *      `?line=northern` alone is ambiguous between them).
 *   2. `?line=<line-slug>` → match record whose `line.slug` equals the param.
 *   3. Else → primary record: cluster's `primaryLineId` (equal across cluster).
 *   4. Else → fall through to first record.
 * - For pre-v1.23 schemas or malformed URLs: fall back to matching `id`.
 *
 * Decoupled from React so callers can unit test without the router.
 */
export interface ResolvedStation {
  readonly station: SchemaStation
  readonly lineForHeader: {
    readonly id: string
    readonly hexColor: string
    readonly localName?: string
  }
  readonly clusterLineIds: readonly string[]
}

export function resolveStation(
  schema: SchemaData | null,
  slug: string | undefined,
  lineParam: string | null,
  stationIdParam: string | null = null,
): ResolvedStation | null {
  if (schema === null || slug === undefined) return null

  // TASK-986: a slug v2 retired opens the station v2 serves it under.
  const current = schema.retiredSlugs?.[slug] ?? slug
  const cluster = schema.stations.filter((s) => s.slug === current)

  if (cluster.length === 0) {
    const byId = schema.stations.find((s) => s.id === decodeURIComponent(slug))
    if (byId === undefined) return null
    return buildResolved(schema, byId, [byId.line.id])
  }

  const clusterLineIds = uniqueSorted(cluster.map((s) => s.line.id))

  let selected: SchemaStation | undefined
  if (stationIdParam !== null) {
    selected = cluster.find((s) => s.id === stationIdParam)
  }
  if (selected === undefined && lineParam !== null) {
    selected = cluster.find((s) => s.line.slug === lineParam)
  }
  if (selected === undefined) {
    const primary = cluster[0].primaryLineId
    if (primary !== undefined) {
      selected = cluster.find((s) => s.line.id === primary)
    }
  }
  if (selected === undefined) {
    selected = cluster[0]
  }
  return buildResolved(schema, selected, clusterLineIds)
}

function buildResolved(
  schema: SchemaData,
  station: SchemaStation,
  clusterLineIds: readonly string[],
): ResolvedStation {
  return {
    station,
    lineForHeader: {
      id: station.line.id,
      hexColor: station.line.hexColor,
      localName: station.line.localName ?? lineLocalName(schema, station.line.id),
    },
    clusterLineIds,
  }
}

function uniqueSorted(ids: readonly string[]): string[] {
  return Array.from(new Set(ids)).sort()
}

export function lineLocalName(schema: SchemaData, lineId: string): string | undefined {
  const match = schema.stations.find((s) => s.line.id === lineId)
  return match?.line.localName
}

export function lineSlugFromId(schema: SchemaData, lineId: string): string {
  const match = schema.stations.find((s) => s.line.id === lineId)
  return match?.line.slug ?? lineId
}

/**
 * Resolve a line's `hexColor` by line id by scanning `schema.stations` for
 * the first record carrying an inline `line` block with that id. Returns
 * `null` when the schema has no station referencing the line — typical
 * during schema drift (a localStorage hydrate hands us a stale lineId no
 * longer present in the schema, or a `RouteFromSelected` event preceded
 * the schema load on a fresh tab).
 *
 * Mirrors {@link lineSlugFromId} — kept consistent so callers that need
 * both slug + color do not have to learn two different lookup shapes.
 * `RouteCard` uses this at render time because
 * `SelectedStationSnapshot` deliberately does NOT carry `lineHexColor`
 * (snapshot is shape-stable; line color is schema-resolved).
 */
export function lineHexColorFromId(
  schema: SchemaData,
  lineId: string,
  theme: 'light' | 'dark',
): string | null {
  const match = schema.stations.find((s) => s.line.id === lineId)
  return match === undefined ? null : lineHexForTheme(match.line, theme)
}

/**
 * Resolve a station from a single slug string, without line/id
 * disambiguation. Convenience wrapper around `resolveStation` for callers
 * that have nothing but a slug (e.g. localStorage hydrate when no URL
 * state is available).
 *
 * Use only where collisions are tolerable — for a multi-line cluster the
 * resolver falls back to `primaryLineId` (or the first record), so two
 * lines through the same interchange will silently pick one. Callers
 * that need exact disambiguation must use `resolveStation` with the
 * `?line=`/`?station=` parameters.
 */
export function resolveStationBySlug(
  schema: SchemaData | null,
  slug: string,
): ResolvedStation | null {
  return resolveStation(schema, slug, null, null)
}

/**
 * Resolve a station by its exact `id`. Used by the carousel
 * From/To handlers to fetch the schema record before dispatching
 * `RouteFromSelected` / `RouteToSelected` — the carousel only carries
 * a station id, not the full snapshot, so the host re-resolves.
 *
 * Returns `null` for ids that do not appear in the schema (schema
 * drift, or callers passing an id that pre-dates a schema reload).
 * The cluster line ids are computed from the matched station's slug
 * so the resolved record carries the same context `resolveStation`
 * would produce.
 */
export function resolveStationById(
  schema: SchemaData | null,
  stationId: string,
): ResolvedStation | null {
  if (schema === null) return null
  const station = schema.stations.find((s) => s.id === stationId)
  if (station === undefined) return null
  const cluster = schema.stations.filter((s) => s.slug === station.slug)
  const clusterLineIds =
    cluster.length === 0 ? [station.line.id] : uniqueSorted(cluster.map((s) => s.line.id))
  return buildResolved(schema, station, clusterLineIds)
}
